import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Home,
  LogOut,
  FileText,
  Users,
  DollarSign,
  TrendingUp,
  Settings,
  Menu,
  X,
  Briefcase,
  Plus,
  Search,
  Phone,
  Mail,
  MapPin,
  CheckCircle,
  AlertCircle,
  Edit2,
  Trash2,
  User,
  CreditCard,
  Target,
  ChevronRight,
  Building2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Realtor Sidebar
function RealtorSidebar({ realtor, onLogout }) {
  const [collapsed, setCollapsed] = useState(false);
  
  const navItems = [
    { icon: Home, label: "Dashboard", path: "/realtor/dashboard" },
    { icon: FileText, label: "Listings", path: "/realtor/listings" },
    { icon: Briefcase, label: "Transactions", path: "/realtor/transactions" },
    { icon: Users, label: "Buyers", path: "/realtor/buyers" },
    { icon: Building2, label: "Sellers", path: "/realtor/sellers" },
    { icon: DollarSign, label: "Commission", path: "/realtor/commission" },
    { icon: TrendingUp, label: "Analytics", path: "/realtor/analytics" },
    { icon: Settings, label: "Settings", path: "/realtor/settings" },
  ];

  return (
    <aside className={`fixed left-0 top-0 h-full bg-black border-r border-white/[0.06] transition-all z-40 ${
      collapsed ? "w-16" : "w-64"
    }`}>
      <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
        {!collapsed && (
          <Link to="/realtor/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gold/10 border border-gold/30 flex items-center justify-center">
              <Home className="w-4 h-4 text-gold" />
            </div>
            <span className="font-heading font-bold text-white">REALTOR</span>
          </Link>
        )}
        <button onClick={() => setCollapsed(!collapsed)} className="text-white/50 hover:text-white p-1">
          {collapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
        </button>
      </div>

      <nav className="p-2 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = window.location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 transition-all duration-300 ${
                isActive ? "bg-gold/10 text-gold border-l-2 border-gold" : "text-white/50 hover:text-white hover:bg-[#0A0A0A]/80 backdrop-blur-sm"
              }`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="text-sm">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {!collapsed && (
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/[0.06]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#0A0A0A]/80 backdrop-blur-sm rounded-full flex items-center justify-center">
              <span className="text-gold font-bold">{realtor?.name?.charAt(0) || "R"}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{realtor?.name}</p>
              <p className="text-white/40 text-xs truncate">{realtor?.brokerage || "Agent"}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 text-white/50 hover:text-[#FF3B30] py-2 border border-white/[0.08] hover:border-red-500/50 transition-all duration-300"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      )}
    </aside>
  );
}

// Status Badge
function StatusBadge({ status }) {
  const colors = {
    active: "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-emerald-500/30",
    under_contract: "bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] border-blue-500/30",
    closed: "bg-white/20/20 text-white/50 border-white/[0.15]/30",
    inactive: "bg-red-500/20 text-[#FF3B30] border-red-500/30"
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-medium border ${colors[status] || colors.inactive}`}>
      {status?.replace(/_/g, " ").toUpperCase()}
    </span>
  );
}

// Pre-Approval Badge
function PreApprovalBadge({ preApproved, amount }) {
  if (preApproved) {
    return (
      <div className="flex items-center gap-1 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">
        <CheckCircle className="w-4 h-4" />
        <span className="text-xs">Pre-Approved: {formatCurrency(amount)}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]">
      <AlertCircle className="w-4 h-4" />
      <span className="text-xs">Not Pre-Approved</span>
    </div>
  );
}

// Buyer Card
function BuyerCard({ buyer, onEdit, onDelete }) {
  return (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] hover:border-gold/30 transition-all duration-300" data-testid={`buyer-card-${buyer.buyer_id}`}>
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center border border-white/[0.08]">
              <User className="w-6 h-6 text-gold" />
            </div>
            <div>
              <h3 className="text-white font-medium">{buyer.name}</h3>
              <div className="flex items-center gap-3 mt-1">
                {buyer.email && (
                  <a href={`mailto:${buyer.email}`} className="text-white/50 text-xs flex items-center gap-1 hover:text-gold">
                    <Mail className="w-3 h-3" /> {buyer.email}
                  </a>
                )}
              </div>
            </div>
          </div>
          <StatusBadge status={buyer.status} />
        </div>

        {/* Budget & Pre-Approval */}
        <div className="bg-black border border-white/[0.08] p-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/50 text-sm">Budget Range</span>
            <span className="text-white font-mono">
              {formatCurrency(buyer.budget_min)} - {formatCurrency(buyer.budget_max)}
            </span>
          </div>
          <PreApprovalBadge preApproved={buyer.pre_approved} amount={buyer.pre_approval_amount} />
        </div>

        {/* Target Areas */}
        {buyer.target_areas?.length > 0 && (
          <div className="mb-3">
            <p className="text-white/50 text-xs mb-2">Target Areas</p>
            <div className="flex flex-wrap gap-1">
              {buyer.target_areas.map((area, idx) => (
                <span key={idx} className="px-2 py-0.5 bg-black border border-white/[0.08] text-white/70 text-xs">
                  {area}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Property Types */}
        {buyer.property_types?.length > 0 && (
          <div className="mb-3">
            <p className="text-white/50 text-xs mb-2">Looking For</p>
            <div className="flex flex-wrap gap-1">
              {buyer.property_types.map((type, idx) => (
                <span key={idx} className="px-2 py-0.5 bg-gold/10 border border-gold/30 text-gold text-xs">
                  {type}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Contact Info */}
        <div className="flex items-center gap-4 pt-3 border-t border-white/[0.08]">
          {buyer.phone && (
            <a href={`tel:${buyer.phone}`} className="flex items-center gap-1 text-white/50 hover:text-gold text-sm">
              <Phone className="w-4 h-4" /> {buyer.phone}
            </a>
          )}
          <div className="flex-1" />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onEdit(buyer)}
            className="text-white/50 hover:text-white"
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(buyer.buyer_id)}
            className="text-white/50 hover:text-[#FF3B30]"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Add/Edit Buyer Modal
function BuyerFormModal({ isOpen, onClose, buyer, onSave }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    pre_approved: false,
    pre_approval_amount: 0,
    budget_min: 0,
    budget_max: 0,
    target_areas: [],
    property_types: [],
    assigned_lender: "",
    notes: "",
    status: "active"
  });
  const [targetAreaInput, setTargetAreaInput] = useState("");
  const [loading, setSaving] = useState(false);

  useEffect(() => {
    if (buyer) {
      setForm({
        name: buyer.name || "",
        email: buyer.email || "",
        phone: buyer.phone || "",
        pre_approved: buyer.pre_approved || false,
        pre_approval_amount: buyer.pre_approval_amount || 0,
        budget_min: buyer.budget_min || 0,
        budget_max: buyer.budget_max || 0,
        target_areas: buyer.target_areas || [],
        property_types: buyer.property_types || [],
        assigned_lender: buyer.assigned_lender || "",
        notes: buyer.notes || "",
        status: buyer.status || "active"
      });
    } else {
      setForm({
        name: "",
        email: "",
        phone: "",
        pre_approved: false,
        pre_approval_amount: 0,
        budget_min: 0,
        budget_max: 0,
        target_areas: [],
        property_types: [],
        assigned_lender: "",
        notes: "",
        status: "active"
      });
    }
  }, [buyer, isOpen]);

  const handleAddTargetArea = () => {
    if (targetAreaInput.trim() && !form.target_areas.includes(targetAreaInput.trim())) {
      setForm({ ...form, target_areas: [...form.target_areas, targetAreaInput.trim()] });
      setTargetAreaInput("");
    }
  };

  const handleRemoveTargetArea = (area) => {
    setForm({ ...form, target_areas: form.target_areas.filter(a => a !== area) });
  };

  const handlePropertyTypeToggle = (type) => {
    if (form.property_types.includes(type)) {
      setForm({ ...form, property_types: form.property_types.filter(t => t !== type) });
    } else {
      setForm({ ...form, property_types: [...form.property_types, type] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form, buyer?.buyer_id);
      onClose();
    } catch (err) {
      toast.error("Failed to save buyer");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const propertyTypes = ["SFH", "Condo", "Townhouse", "Multi-Family", "Land"];

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] w-full max-w-lg my-8">
        <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-white">
            {buyer ? "Edit Buyer" : "Add Buyer"}
          </h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-white/50">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="bg-black border-white/[0.08] text-white mt-1"
                placeholder="John Smith"
              />
            </div>
            <div>
              <Label className="text-white/50">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-black border-white/[0.08] text-white mt-1"
                placeholder="john@email.com"
              />
            </div>
            <div>
              <Label className="text-white/50">Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-black border-white/[0.08] text-white mt-1"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          {/* Budget */}
          <div className="bg-black border border-white/[0.08] p-4">
            <h3 className="text-white font-medium mb-3 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-gold" /> Budget & Financing
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/50">Min Budget</Label>
                <Input
                  type="number"
                  value={form.budget_min}
                  onChange={(e) => setForm({ ...form, budget_min: parseFloat(e.target.value) || 0 })}
                  className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white/50">Max Budget</Label>
                <Input
                  type="number"
                  value={form.budget_max}
                  onChange={(e) => setForm({ ...form, budget_max: parseFloat(e.target.value) || 0 })}
                  className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white mt-1"
                />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <input
                type="checkbox"
                id="pre_approved"
                checked={form.pre_approved}
                onChange={(e) => setForm({ ...form, pre_approved: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="pre_approved" className="text-white">Pre-Approved</Label>
            </div>
            {form.pre_approved && (
              <div className="mt-3">
                <Label className="text-white/50">Pre-Approval Amount</Label>
                <Input
                  type="number"
                  value={form.pre_approval_amount}
                  onChange={(e) => setForm({ ...form, pre_approval_amount: parseFloat(e.target.value) || 0 })}
                  className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white mt-1"
                />
              </div>
            )}
          </div>

          {/* Target Areas */}
          <div>
            <Label className="text-white/50 flex items-center gap-2 mb-2">
              <MapPin className="w-4 h-4" /> Target Areas
            </Label>
            <div className="flex gap-2 mb-2">
              <Input
                value={targetAreaInput}
                onChange={(e) => setTargetAreaInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddTargetArea())}
                placeholder="Add area (e.g., Downtown Austin)"
                className="bg-black border-white/[0.08] text-white"
              />
              <Button type="button" onClick={handleAddTargetArea} className="bg-gold hover:bg-gold/90 text-black">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {form.target_areas.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {form.target_areas.map((area, idx) => (
                  <span key={idx} className="px-2 py-1 bg-black border border-white/[0.08] text-white/70 text-sm flex items-center gap-1">
                    {area}
                    <button type="button" onClick={() => handleRemoveTargetArea(area)} className="text-white/40 hover:text-[#FF3B30]">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Property Types */}
          <div>
            <Label className="text-white/50 flex items-center gap-2 mb-2">
              <Target className="w-4 h-4" /> Property Types
            </Label>
            <div className="flex flex-wrap gap-2">
              {propertyTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handlePropertyTypeToggle(type)}
                  className={`px-3 py-1.5 border text-sm transition-all duration-300 ${
                    form.property_types.includes(type)
                      ? "bg-gold/20 border-gold text-gold"
                      : "bg-black border-white/[0.08] text-white/50 hover:border-white/[0.15]"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Lender */}
          <div>
            <Label className="text-white/50">Assigned Lender</Label>
            <Input
              value={form.assigned_lender}
              onChange={(e) => setForm({ ...form, assigned_lender: e.target.value })}
              className="bg-black border-white/[0.08] text-white mt-1"
              placeholder="Lender name or contact"
            />
          </div>

          {/* Status */}
          <div>
            <Label className="text-white/50">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="bg-black border-white/[0.08] text-white mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="under_contract">Under Contract</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-white/50">Notes</Label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="w-full bg-black border border-white/[0.08] text-white p-3 mt-1"
              placeholder="Additional notes about this buyer..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.08]">
            <Button type="button" variant="outline" onClick={onClose} className="border-white/[0.1] text-white/70 rounded-none">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-gold hover:bg-gold/90 text-black rounded-none">
              {loading ? "Saving..." : buyer ? "Update Buyer" : "Add Buyer"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function RealtorBuyersPage() {
  const navigate = useNavigate();
  const [realtor, setRealtor] = useState(null);
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBuyer, setEditingBuyer] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, buyersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/realtor/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/realtor/buyers`, { withCredentials: true })
      ]);
      setRealtor(profileRes.data);
      setBuyers(buyersRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/realtor");
      } else {
        toast.error("Failed to load buyers");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/realtor/auth/logout`, {}, { withCredentials: true });
      navigate("/realtor");
    } catch (error) {
      navigate("/realtor");
    }
  };

  const handleSaveBuyer = async (form, buyerId) => {
    try {
      if (buyerId) {
        await axios.put(`${BACKEND_URL}/api/realtor/buyers/${buyerId}`, form, { withCredentials: true });
        toast.success("Buyer updated");
      } else {
        await axios.post(`${BACKEND_URL}/api/realtor/buyers`, form, { withCredentials: true });
        toast.success("Buyer added");
      }
      fetchData();
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteBuyer = async (buyerId) => {
    if (!confirm("Are you sure you want to remove this buyer?")) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/realtor/buyers/${buyerId}`, { withCredentials: true });
      toast.success("Buyer removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove buyer");
    }
  };

  const handleEditBuyer = (buyer) => {
    setEditingBuyer(buyer);
    setShowModal(true);
  };

  const filteredBuyers = buyers.filter((b) => {
    const matchesSearch = !searchTerm ||
      b.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.target_areas?.some(a => a.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === "all" || b.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Stats
  const activeCount = buyers.filter(b => b.status === "active").length;
  const preApprovedCount = buyers.filter(b => b.pre_approved).length;
  const underContractCount = buyers.filter(b => b.status === "under_contract").length;
  const avgBudget = buyers.length > 0 
    ? buyers.reduce((sum, b) => sum + (b.budget_max || 0), 0) / buyers.length 
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="realtor-buyers-page">
      <RealtorSidebar realtor={realtor} onLogout={handleLogout} />

      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white uppercase">Buyers Pipeline</h1>
            <p className="text-white/50 mt-1">{buyers.length} total buyers</p>
          </div>
          <Button
            onClick={() => {
              setEditingBuyer(null);
              setShowModal(true);
            }}
            className="gold-gradient-btn rounded-none font-bold"
            data-testid="add-buyer-btn"
          >
            <Plus className="w-5 h-5 mr-2" /> Add Buyer
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
              <span className="text-white/50 text-sm">Active Buyers</span>
            </div>
            <p className="text-2xl font-bold text-white">{activeCount}</p>
          </div>
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-gold" />
              <span className="text-white/50 text-sm">Pre-Approved</span>
            </div>
            <p className="text-2xl font-bold text-white">{preApprovedCount}</p>
          </div>
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="w-5 h-5 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" />
              <span className="text-white/50 text-sm">Under Contract</span>
            </div>
            <p className="text-2xl font-bold text-white">{underContractCount}</p>
          </div>
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]" />
              <span className="text-white/50 text-sm">Avg. Budget</span>
            </div>
            <p className="text-2xl font-bold text-white font-mono">{formatCurrency(avgBudget)}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 mb-6 flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
              <Input
                placeholder="Search by name, email, or area..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-black border-white/[0.08] text-white pl-10"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-black border-white/[0.08] text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="under_contract">Under Contract</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Buyers Grid */}
        {filteredBuyers.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredBuyers.map((buyer) => (
              <BuyerCard
                key={buyer.buyer_id}
                buyer={buyer}
                onEdit={handleEditBuyer}
                onDelete={handleDeleteBuyer}
              />
            ))}
          </div>
        ) : (
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-12 text-center">
            <Users className="w-12 h-12 text-white/30 mx-auto mb-3" />
            <p className="text-white/50">
              {searchTerm || statusFilter !== "all"
                ? "No buyers match your filters"
                : "No buyers in your pipeline yet"}
            </p>
            {!searchTerm && statusFilter === "all" && (
              <Button
                onClick={() => {
                  setEditingBuyer(null);
                  setShowModal(true);
                }}
                className="mt-4 gold-gradient-btn rounded-none"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Your First Buyer
              </Button>
            )}
          </div>
        )}
      </main>

      <BuyerFormModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingBuyer(null);
        }}
        buyer={editingBuyer}
        onSave={handleSaveBuyer}
      />
    </div>
  );
}
